import React from "react";
import styled from "styled-components";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import i18n from "i18next";
import { hashParams, modalValues } from "routes";
import { withRouter } from "@kubera/common";
import PrimaryButton from "components/button/PrimaryButton";
import SecondaryButton from "components/button/SecondaryButton";
import { addKeyboardEventListener, removeKeyboardEventListener } from "utilities/EventManager";
import { isMobile } from "@kubera/common";

const isMobileDevice = isMobile();

const GenericDialog = styled(Dialog)`
  width: ${props => (props.isMobile ? "90%" : props.dialogWidth ? props.dialogWidth : "34.375rem")};
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 10.1875rem;
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: ${props => (props.isMobile ? "1.875rem" : "3.125rem 2.8125rem 3.125rem 2.8125rem")};
`;

const Title = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: ${props => (props.isMobile ? "0.9375rem" : "1.375rem")};
  line-height: 1.6875rem;
  display: flex;
  align-items: flex-end;
  font-feature-settings: "ss01" on;
  white-space: pre-wrap;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex: 1;
  margin-top: ${props => (props.hasTitle ? "0.625rem" : "0")};
`;

const Description = styled.div`
  flex: 1;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 150%;
  font-feature-settings: "ss01" on;
  white-space: pre-wrap;
  ${props => (props.descriptionStyle ? props.descriptionStyle : "")}
`;

const Icon = styled.div`
  background-color: transparent;
  background-image: url("${props => props.icon}");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  margin-left: 1.875rem;
  ${props => (props.iconStyle ? props.iconStyle : "")}
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 1.75rem;
`;

const PositiveButton = styled(PrimaryButton)`
  margin-right: 1.25rem;
  font-size: ${isMobileDevice ? "0.875rem" : "0.75rem"};
  min-width: ${props => (props.minWidth ? props.minWidth : undefined)};
`;

const NegativeButton = styled(SecondaryButton)``;

const Note = styled.div`
  margin-top: 0.9375rem;
  color: rgba(0, 0, 0, 0.7);
  font-feature-settings: "ss01" on;
  font-size: 0.75rem;
  font-style: italic;
  font-weight: 400;
  line-height: 150%;
`;

class GenericMessageDialog extends React.Component {
  static show = (history, location) => {
    history.push({
      ...location,
      hash: `${hashParams.MODAL}=${modalValues.GENERIC_ERROR}`
    });
  };

  constructor(props) {
    super(props);

    this.handleOverlayDismiss = this.handleOverlayDismiss.bind(this);
    this.handlePositiveButtonClick = this.handlePositiveButtonClick.bind(this);
    this.handleNegativeButtonClick = this.handleNegativeButtonClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    addKeyboardEventListener(this.handleKeyDown);
  }

  componentWillUnmount() {
    removeKeyboardEventListener(this.handleKeyDown);
  }

  handleKeyDown(e) {
    if (e.key === "Enter") {
      this.handlePositiveButtonClick();
      return true;
    }
  }

  handleOverlayDismiss() {
    if (this.props.canUserDismiss === false) {
      return;
    }

    if (this.props.onOverlayDismissClick) {
      this.props.onOverlayDismissClick();
    } else {
      DialogOverlay.dismiss(this.props.history, this.props.location);
    }
  }

  handlePositiveButtonClick() {
    if (this.props.onPositiveButtonClick) {
      this.props.onPositiveButtonClick();
    } else {
      this.handleOverlayDismiss();
    }
  }

  handleNegativeButtonClick() {
    if (this.props.onNegativeButtonClick) {
      this.props.onNegativeButtonClick();
    } else {
      this.handleOverlayDismiss();
    }
  }

  render() {
    const title = this.props.title;
    const description = this.props.description;
    const positiveButtonTitle = this.props.positiveButtonTitle
      ? this.props.positiveButtonTitle
      : i18n.t("ok").toUpperCase();
    const negativeButtonTitle = this.props.negativeButtonTitle;
    const icon = this.props.icon;
    const dialogWidth = this.props.dialogWidth;

    return (
      <DialogOverlay onDismiss={this.handleOverlayDismiss}>
        <GenericDialog className={this.props.className} isMobile={isMobileDevice} dialogWidth={dialogWidth}>
          <Container isMobile={isMobileDevice}>
            <Title isMobile={isMobileDevice}>{title}</Title>
            <DescriptionContainer hasTitle={!title === false}>
              <Description
                descriptionStyle={this.props.descriptionStyle}
                dangerouslySetInnerHTML={{
                  __html: description
                }}
              />
              <Icon icon={icon} iconStyle={this.props.iconStyle} />
            </DescriptionContainer>
            <ButtonContainer>
              <PositiveButton
                data-cy="genericPositive"
                onClick={this.handlePositiveButtonClick}
                title={positiveButtonTitle}
                isLoading={this.props.positiveButtonLoading}
                minWidth={this.props.minWidth}
              />
              {!negativeButtonTitle === false && (
                <NegativeButton
                  data-cy="genericNegative"
                  onClick={this.handleNegativeButtonClick}
                  title={negativeButtonTitle}
                  isLoading={this.props.negativeButtonLoading}
                />
              )}
            </ButtonContainer>
            {this.props.note && <Note>{this.props.note}</Note>}
          </Container>
        </GenericDialog>
      </DialogOverlay>
    );
  }
}

export default withRouter(GenericMessageDialog);
